import { useQuery } from '@tanstack/react-query'
import AnalyticsApi from '@/infrastructure/api/analytics'
import { useAnalyticsScope } from '@/hooks'
import { useFilter } from '@/application/use-filter'
import { toCountryCodes } from '@/domain/countries'
import { useCountries } from '@/application/queries'

function getQueryKey(
  ecommerceIds: string[],
  startDate?: string,
  endDate?: string,
  countries?: string[],
  collections?: string[],
  chartMetric?:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
) {
  return [
    'periodReturnRate',
    ecommerceIds,
    startDate,
    endDate,
    countries,
    collections,
    chartMetric
  ]
}

export function useMonthlyReturnRate(
  startDate?: string,
  endDate?: string,
  chartMetric?:
    | 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    | 'RETURN_RATE_CHART_METRIC_QUANTITY'
    | 'RETURN_RATE_CHART_METRIC_AMOUNT'
) {
  const { ecommerceIds } = useAnalyticsScope()
  const { countries, loadingCountries } = useCountries()

  const { getFilterValues: getSelectedCountries } = useFilter('countries')
  const selectedCountries = getSelectedCountries()
  const { getFilterValues: getSelectedCollections } = useFilter('collections')
  const selectedCollections = getSelectedCollections()

  async function callPeriodReturnRate() {
    if (!ecommerceIds.length) return

    return AnalyticsApi.getPeriodReturnRate({
      ecommerceId: ecommerceIds[0],
      ecommerceIds,
      startDate,
      endDate,
      countries: toCountryCodes(selectedCountries, countries),
      collections: selectedCollections,
      chartMetric: chartMetric ?? 'RETURN_RATE_CHART_METRIC_UNSPECIFIED'
    })
  }

  const periodReturnRateQuery = useQuery({
    queryKey: getQueryKey(
      ecommerceIds,
      startDate,
      endDate,
      selectedCountries,
      selectedCollections,
      chartMetric
    ),
    queryFn: callPeriodReturnRate,
    staleTime: 1000 * 60 * 15, // 15 minutes
    enabled:
      ecommerceIds.length > 0 && !!startDate && !!endDate && !!countries.length
  })

  const itemsPurchasedByMonth =
    periodReturnRateQuery.data?.itemsPurchasedByMonth ?? []
  const itemsReturnedByMonth =
    periodReturnRateQuery.data?.itemsReturnedByMonth ?? []
  const itemsReturnedSplitByMonth =
    periodReturnRateQuery.data?.itemsReturnedSplitByMonth ?? []
  const returnRatesByMonth =
    periodReturnRateQuery.data?.returnRatesByMonth ?? []
  const refundRatesByMonth =
    periodReturnRateQuery.data?.refundRatesByMonth ?? []
  const monthNames = periodReturnRateQuery.data?.monthNames ?? []

  const shopCurrency = periodReturnRateQuery.data?.shopCurrency ?? 'EUR'

  return {
    itemsPurchasedByMonth,
    itemsReturnedByMonth,
    itemsReturnedSplitByMonth,
    returnRatesByMonth,
    refundRatesByMonth,
    monthNames,
    shopCurrency,
    loading: periodReturnRateQuery.isLoading || loadingCountries
  }
}
